import { type Currency } from "~/api/products/types";
import { priceFormatter } from "~/utils/price-formatter";

import { type KioskDisplayBasketItem } from "./types";

type ResolveDisplayDepositPricesArgs = Pick<
  KioskDisplayBasketItem,
  | "productDepositTotalInclVat"
  | "productDepositTotalExclVat"
  | "variantDepositTotals"
> & {
  showPricesInclVat: boolean;
  hidePrices: boolean;
  currency: Currency;
  locale: string | null;
};

/**
 * The deposit rows of a basket line on the till's display basis: the
 * product's own share and each picked choice's share by choice id. A share of
 * zero has no row, and a till that hides prices shows none.
 */
export function resolveDisplayDepositPrices({
  showPricesInclVat,
  hidePrices,
  currency,
  locale,
  productDepositTotalInclVat,
  productDepositTotalExclVat,
  variantDepositTotals
}: ResolveDisplayDepositPricesArgs): {
  depositPrice: string | null;
  variantDepositPrices: Record<string, string>;
} {
  const format = (share: { inclVat: number; exclVat: number }) => {
    const value = showPricesInclVat ? share.inclVat : share.exclVat;
    return value > 0 && !hidePrices
      ? priceFormatter({ value, currency, locale })
      : null;
  };

  return {
    depositPrice: format({
      inclVat: productDepositTotalInclVat,
      exclVat: productDepositTotalExclVat
    }),
    variantDepositPrices: Object.fromEntries(
      Object.entries(variantDepositTotals).flatMap(([choiceId, share]) => {
        const price = format(share);
        return price ? [[choiceId, price] as const] : [];
      })
    )
  };
}
